import React, { useEffect, useState } from 'react';

// Components
import { FormIcon, SMPFlex, SMPImage, SMPText, styled } from '../../../index';

// Types
import { INormalizedStanding, INormalizedStandingColumn, INormalizedTeamForm } from '../../../../types';
import { CSS } from '../../../../theme/stitches.config';

// Constants
import { SMPLinkWrapper } from '../../atoms/SMPLinkWrapper';
import { STANDINGS_DATA_ELEMENTS_BREAKPOINT } from '../../../../constants/generic';

export interface ITableProps {
  rows?: INormalizedStanding[];
  isSmallSize: boolean;
  noDataMessage?: string;
  columnsLength: number;
  handleClick?: (id: string | number, name: string) => void;
  handleTeamClick?: (id: string | number, name: string) => void;
  containerSize?: number;
  displayImage?: boolean;
}

interface IDataEntity {
  type: string;
  value: INormalizedTeamForm[] | string;
  link?: string;
  urlTarget?: string;
  countryName?: string;
  countryFlag?: string;
  teamName?: string;
  teamLink?: string;
  teamId?: string;
  teamLogo?: string;
  css?: CSS;
  eventValues: { id: string | number; name: string };
  containerSize?: number;
}

const TableRow = styled.withConfig({ componentId: 'CommonWidget', displayName: 'TableRow' })('tr', {
  height: '45px',
  position: 'relative',
  '&::after': {
    content: '',
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    borderBottom: '1px solid $primaryBackgroundColor',
    zIndex: 100,
  },
});

const StickyColD = styled.withConfig({ componentId: 'CommonWidget', displayName: 'StickyColD' })('td', {
  position: 'sticky',
  width: '100%',
  left: '0',
  bc: '$rowBackgroundColor',
  boxSizing: 'border-box',
  '&::before': {
    content: '',
    width: '$1',
    top: '4%',
    height: '90%',
    position: 'absolute',
    borderRadius: '0 $3 $3 0',
  },
  variants: {
    rule: {
      top: {
        '&::before': {
          background: '$top',
        },
      },
      topplayoff: {
        '&::before': {
          background: '$topPlayoff',
        },
      },
      relegation: {
        '&::before': {
          background: '$relegationColor',
        },
      },
      relegationplayoff: {
        '&::before': {
          background: '$relegationPlayoff',
        },
      },
      promotion: {
        '&::before': {
          background: '$promotion',
        },
      },
      promotionplayoff: {
        '&::before': {
          background: '$promotionPlayoff',
        },
      },
      championshipplayoff: {
        '&::before': {
          background: '$championshipPlayoff',
        },
      },
      tiertwo: {
        '&::before': {
          background: '$tierTwo',
        },
      },
      tiertwoplayoff: {
        '&::before': {
          background: '$tierTwoPlayoff',
        },
      },
      none: {
        '&::before': {
          background: 'inherit',
        },
      },
    },
    highlighted: {
      true: {
        backgroundColor: '$highLighted',
      },
    },
  },
});

const TableD = styled.withConfig({ componentId: 'CommonWidget', displayName: 'TableD' })('td', {
  bc: '$rowBackgroundColor',
  py: '12px',
  ml: '$3',
});

const statDefaultColumnsMaxWidth = (containerWidth: number | undefined) => {
  if (containerWidth) {
    if (containerWidth < 300) {
      return '185px';
    } else if (containerWidth < STANDINGS_DATA_ELEMENTS_BREAKPOINT - 80) {
      return '225px';
    } else if (containerWidth < STANDINGS_DATA_ELEMENTS_BREAKPOINT - 55) {
      return '225px';
    } else if (containerWidth < STANDINGS_DATA_ELEMENTS_BREAKPOINT - 35) {
      return '250px';
    } else if (containerWidth < STANDINGS_DATA_ELEMENTS_BREAKPOINT - 20) {
      return '285px';
    } else if (containerWidth < STANDINGS_DATA_ELEMENTS_BREAKPOINT) {
      return '300px';
    }
  }
  return '';
};

export const StatsTableRows = ({
  rows,
  isSmallSize,
  noDataMessage,
  handleClick,
  handleTeamClick,
  columnsLength,
  containerSize,
  displayImage,
}: ITableProps): JSX.Element => {
  const getDataEntity = ({ type, value, css = {}, eventValues, countryName, countryFlag, teamName, teamId, teamLogo }: IDataEntity) => {
    switch (type) {
      case 'text': {
        const data = value as string;

        return (
          <SMPText variant='contrast' size='3' css={css}>
            {data ?? '-'}
          </SMPText>
        );
      }
      case 'link': {
        const data = value as string;

        return (
          <>
            {countryName || teamName ? (
              <SMPFlex
                css={css}
                style={{
                  flexDirection: 'column',
                  marginTop: '8px',
                  marginBottom: '8px',
                  marginLeft: isSmallSize ? '0' : '$2',
                  gap: '2px',
                }}
              >
                <SMPText
                  variant='contrast'
                  size='3'
                  css={{
                    ...css,
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    width: '100%',
                    maxWidth: '135px',
                    lineHeight: 'normal',
                  }}
                  onClick={() => handleClick && handleClick(eventValues.id, eventValues.name)}
                >
                  {data}
                </SMPText>
                {countryName && (
                  <SMPFlex css={{ alignItems: 'center', gap: '2px', display: 'flex' }}>
                    <SMPImage
                      variant='livescoreEntityLogo'
                      src={countryFlag}
                      alt={`${countryName} Logo`}
                      css={{ width: '16px', height: '16px', br: '$round' }}
                    />
                    <SMPText
                      variant='contrast'
                      size='2'
                      css={{
                        ...css,
                        color: '$playerTeamColor',
                        marginLeft: '4px',
                      }}
                    >
                      {countryName}
                    </SMPText>
                  </SMPFlex>
                )}
                {teamName && (
                  <SMPFlex css={{ alignItems: 'center', gap: '2px', display: 'flex' }}>
                    <SMPImage
                      variant='livescoreEntityLogo'
                      src={teamLogo}
                      alt={`${teamName} Logo`}
                      css={{ width: '16px', height: '16px', br: '$round' }}
                    />
                    <SMPText
                      variant='contrast'
                      size='2'
                      css={{
                        ...css,
                        color: '$playerTeamColor',
                        marginLeft: '4px',
                      }}
                      onClick={() => teamId && handleTeamClick && handleTeamClick(teamId, teamName)}
                    >
                      {teamName}
                    </SMPText>
                  </SMPFlex>
                )}
              </SMPFlex>
            ) : (
              <SMPText
                variant='contrast'
                size='3'
                css={{
                  ...css,
                  whiteSpace: 'normal', // Allow the text to wrap
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box', // Display as a WebKit box
                  WebkitLineClamp: 2, // Limit the number of lines to 2
                  WebkitBoxOrient: 'vertical', // Set the box orientation to vertical
                  width: '100%', // Ensure it takes up the full width
                  maxWidth: '135px', // Adjust max-width as necessary
                  lineHeight: 'normal',
                }}
                onClick={() => handleClick && handleClick(eventValues?.id, eventValues?.name)}
              >
                {data}
              </SMPText>
            )}
          </>
        );
      }
      case 'image': {
        const data = value as string;

        return (
          <SMPImage
            variant='livescoreEntityLogo'
            src={data}
            alt={`${data} Logo`}
            style={{
              display: displayImage ? 'block' : 'none',
            }}
            css={{
              width: '42px',
              height: '42px',
              '@bp0': { display: 'none' },
              borderRadius: '$round',
              ...css,
            }}
          />
        );
      }
      case 'form': {
        const data = value as INormalizedTeamForm[];
        return (
          <>
            <SMPFlex justify='start' variant='formWrapper'>
              {data?.map((form: INormalizedTeamForm) => (
                <FormIcon key={form.matchLink} form={form} />
              ))}
            </SMPFlex>
          </>
        );
      }
    }
  };

  const [defColumnsMaxWidth, setDefColumnsMaxWidth] = useState(statDefaultColumnsMaxWidth(containerSize));

  useEffect(() => {
    if (containerSize) {
      setDefColumnsMaxWidth(statDefaultColumnsMaxWidth(containerSize));
    }
  }, [containerSize]);

  if (rows && rows.length > 0) {
    return (
      <>
        {rows?.map((item, index) => {
          const deviceColumns = isSmallSize ? item?.mobileColumns : item?.desktopColumns;
          const itemLink = deviceColumns?.find((column) => column.type === 'link');

          const eventLinkProps = {
            href: itemLink?.link,
            target: itemLink?.urlTarget,
            onClick: () =>
              handleClick &&
              itemLink?.eventValues?.id &&
              itemLink?.eventValues?.name &&
              handleClick(itemLink?.eventValues?.id, itemLink?.eventValues?.name),
          };

          return (
            <SMPLinkWrapper key={`standings-rank-${index}`} {...eventLinkProps}>
              <TableRow>
                <StickyColD
                  css={{
                    '@bp0': { zIndex: '20' },
                    '@bp2': { zIndex: isSmallSize ? '20' : '0' },
                  }}
                  rule={item.ruleType}
                  highlighted={item.isHighlighted}
                  className={item.isHighlighted ? 'highlighted-entity' : ''}
                >
                  <SMPFlex variant='centeredRow' style={{ maxWidth: defColumnsMaxWidth, gap: '2px' }}>
                    {deviceColumns
                      ? deviceColumns.slice(0, 3).map((column: INormalizedStandingColumn, index) => {
                          return (
                            <React.Fragment key={index}>
                              {getDataEntity({
                                type: column.type,
                                value: column.value as INormalizedTeamForm[] | string,
                                css: column.css as CSS,
                                eventValues: column.eventValues as { id: string | number; name: string },
                                link: column.link,
                                urlTarget: column.urlTarget,
                                countryName: column.countryName,
                                countryFlag: column.countryFlag,
                                teamName: column.teamName,
                                teamLink: column.teamLink,
                                teamLogo: column.teamLogo,
                              })}
                            </React.Fragment>
                          );
                        })
                      : null}
                  </SMPFlex>
                </StickyColD>

                {deviceColumns
                  ? deviceColumns.slice(3, deviceColumns.length).map((column: INormalizedStandingColumn, index) => {
                      return (
                        <TableD
                          key={index}
                          css={{
                            backgroundColor: item.isHighlighted ? '$highLighted' : '',
                          }}
                          className={item.isHighlighted ? 'highlighted-entity' : ''}
                        >
                          {getDataEntity({
                            type: column.type,
                            value: column.value as INormalizedTeamForm[] | string,
                            css: column.css as CSS,
                            eventValues: column.eventValues as { id: string | number; name: string },
                            link: column.link,
                            urlTarget: column.urlTarget,
                            countryName: column.countryName,
                            countryFlag: column.countryFlag,
                            teamName: column.teamName,
                            teamLogo: column.teamLogo,
                          })}
                        </TableD>
                      );
                    })
                  : null}
              </TableRow>
            </SMPLinkWrapper>
          );
        })}
      </>
    );
  }

  return (
    <TableRow key='standings-rank-no-data'>
      <TableD key='no-data-table-data' align='center' colSpan={columnsLength}>
        <SMPText variant='contrast' size='3'>
          {noDataMessage ?? 'No data'}
        </SMPText>
      </TableD>
    </TableRow>
  );
};
